import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import paximumLogo from '../assets/paximum.svg';
import paximumswsLogo from '../assets/paximumsws.svg';
import tvLogo from '../assets/tourvisio.svg';
import sejourLogo from '../assets/sejour.svg';
import logo from '../assets/santsgdark.png';
import { apiClient } from '../lib/api-client';
import { Skeleton } from '@/components/ui/skeleton';
import type { Docset } from '../types';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [docsets, setDocsets] = useState<Docset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocsets = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<{ docsets: Docset[] }>('/api/docsets');
        // Order: tourvisio, paximum, paximumsws, sejour
        const sortedDocsets = [...response.docsets].sort((a, b) => {
          const order: Record<string, number> = {
            'tourvisio': 1,
            'paximum': 2,
            'paximumsws': 3,
            'sejour': 4
          };
          const orderA = order[a.id] ?? 99;
          const orderB = order[b.id] ?? 99;
          return orderA - orderB;
        });
        setDocsets(sortedDocsets);
      } catch (err) {
        console.error('Failed to fetch docsets:', err);
        setError('Docset\'ler yüklenirken hata oluştu');
        setDocsets([
          { id: 'paximum', name: 'Paximum API', purpose: 'Paximum is a B2B travel marketplace', default_questions: [] },
          { id: 'tourvisio', name: 'TourVisio API', purpose: 'TourVisio provides comprehensive API solutions', default_questions: [] },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDocsets();
  }, []);

  const handleDocsetSelect = useCallback((docsetId: string) => {
    navigate(`/${docsetId}`);
  }, [navigate]);

  const LogoPlaceholder = memo(({ docsetId, name }: { docsetId: string; name: string }) => {
    const actualLogos: Record<string, string> = {
      'paximum': paximumLogo,
      'paximumsws': paximumswsLogo,
      'tourvisio': tvLogo,
      'sejour': sejourLogo
    };

    if (actualLogos[docsetId]) {
      return (
        <img
          src={actualLogos[docsetId]}
          alt={`${name} Logo`}
          className="w-6 h-6 object-contain"
        />
      );
    }

    return (
      <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 text-white font-bold text-[10px] flex items-center justify-center rounded">
        {name.split(' ').map(word => word[0]).join('').substring(0, 2)}
      </div>
    );
  });

  if (loading) {
    return (
      <main className="flex items-center justify-center min-h-screen bg-background p-md tablet:p-lg">
        <div className="max-w-4xl w-full flex flex-col items-center justify-center">
          <div className="flex flex-col items-center mb-12 tablet:mb-16">
            <img
              src={logo}
              alt="SANTSG Logo"
              width="384"
              height="128"
              className="w-64 tablet:w-96 h-auto object-contain"
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2 tablet:gap-3 px-4 tablet:px-0 max-w-4xl mx-auto">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <Skeleton className="w-6 h-6 rounded" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex items-center justify-center min-h-screen bg-background p-md tablet:p-lg">
      <div className="max-w-4xl w-full flex flex-col items-center justify-center">
        <div className="flex flex-col items-center mb-12 tablet:mb-16">
          <img
            src={logo}
            alt="SANTSG Logo"
            width="384"
            height="128"
            className="w-64 tablet:w-96 desktop:w-112 h-auto object-contain"
          />
        </div>
        {error && (
          <div className="mb-lg p-md bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-center">
            {error}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-2 tablet:gap-3 px-4 tablet:px-0 max-w-4xl mx-auto">
          {docsets.map((docset) => (
            <div
              key={docset.id}
              onClick={() => handleDocsetSelect(docset.id)}
              className="group flex items-center gap-2.5 px-4 py-2 tablet:px-5 tablet:py-2.5
                         rounded-full cursor-pointer 
                         transition-all duration-200 motion-reduce:transition-none 
                         hover:scale-105 motion-reduce:hover:scale-100
                         bg-white/5 hover:bg-white/10 
                         border border-white/10 hover:border-white/20
                         active:scale-100"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDocsetSelect(docset.id);
                }
              }}
              aria-label={`Open ${docset.name} documentation`}
            >
              <LogoPlaceholder docsetId={docset.id} name={docset.name} />
              <span className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                {docset.name.replace(/\s*API$/, '')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default memo(Home);
